import mammoth from 'mammoth';

/**
 * Resume Parser Service
 * Extracts structured information from resume files (PDF, DOC, DOCX)
 * and calculates match scores based on job requirements
 */

export interface ParsedResume {
  extractedText: string;
  name?: string;
  email?: string;
  phone?: string;
  skills: string[];
  experience: string[];
  education: string[];
  summary?: string;
}

export interface SkillMatch {
  skill: string;
  required: boolean;
  matched: boolean;
  score: number;
}

export interface ScreeningResults {
  overallScore: number;
  skillMatches: SkillMatch[];
  experienceMatch: number;
  educationMatch: number;
  keywords: string[];
}

/**
 * Common skills database for matching
 */
const COMMON_SKILLS = [
  // Programming Languages
  'JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'C#', 'Ruby', 'PHP', 'Go', 'Swift', 'Kotlin', 'Rust',
  // Frontend
  'React', 'Vue', 'Angular', 'HTML', 'CSS', 'SCSS', 'Tailwind', 'Bootstrap', 'jQuery', 'Next.js', 'Gatsby',
  // Backend
  'Node.js', 'Express', 'Django', 'Flask', 'Spring', 'Laravel', 'Rails', 'ASP.NET', 'FastAPI',
  // Databases
  'PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'Cassandra', 'Oracle', 'SQL Server', 'SQLite', 'DynamoDB',
  // Cloud & DevOps
  'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Jenkins', 'GitLab CI', 'GitHub Actions', 'Terraform', 'Ansible',
  // Tools
  'Git', 'Linux', 'REST API', 'GraphQL', 'Microservices', 'Agile', 'Scrum', 'JIRA', 'Confluence',
  // Data & Analytics
  'Machine Learning', 'Deep Learning', 'TensorFlow', 'PyTorch', 'Pandas', 'NumPy', 'Data Analysis', 'Statistics',
  // Design
  'Figma', 'Sketch', 'Adobe XD', 'Photoshop', 'Illustrator', 'UI/UX', 'Prototyping', 'Wireframing',
  // Other
  'Leadership', 'Communication', 'Problem Solving', 'Team Work', 'Project Management'
];

/**
 * Extract text from PDF file
 */
async function extractTextFromPDF(file: File): Promise<string> {
  try {
    // For browser environment, we'll use pdf.js
    // Import dynamically to avoid SSR issues
    const pdfjsLib = await import('pdfjs-dist');

    // Set worker source - using local worker file from public folder
    // This avoids CORS issues and CDN loading problems
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item: any) => item.str).join(' ');
      fullText += pageText + '\n';
    }

    return fullText;
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    throw new Error('Failed to extract text from PDF file');
  }
}

/**
 * Extract text from DOCX file
 */
async function extractTextFromDOCX(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  } catch (error) {
    console.error('Error extracting text from DOCX:', error);
    throw new Error('Failed to extract text from DOCX file');
  }
}

/**
 * Extract text from DOC file (older format)
 * Note: DOC parsing is limited in browser, best effort
 */
async function extractTextFromDOC(file: File): Promise<string> {
  try {
    // Try using mammoth (it might work for some DOC files)
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  } catch (error) {
    console.warn('Could not parse DOC file, returning limited text');
    // Fallback: read as text (won't be perfect but better than nothing)
    return 'Unable to fully parse DOC file. Please use PDF or DOCX format for best results.';
  }
}

/**
 * Extract text from resume file based on type
 */
async function extractTextFromFile(file: File): Promise<string> {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();

  if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return await extractTextFromPDF(file);
  } else if (
    fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileName.endsWith('.docx')
  ) {
    return await extractTextFromDOCX(file);
  } else if (fileType === 'application/msword' || fileName.endsWith('.doc')) {
    return await extractTextFromDOC(file);
  } else {
    throw new Error('Unsupported file format. Please use PDF, DOC, or DOCX.');
  }
}

/**
 * Extract email from text
 */
function extractEmail(text: string): string | undefined {
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  const matches = text.match(emailRegex);
  return matches?.[0];
}

/**
 * Extract phone number from text
 * Enhanced to catch multiple phone number formats
 */
function extractPhone(text: string): string | undefined {
  // Multiple patterns to catch various phone formats
  const patterns = [
    // US/Canada format: (555) 123-4567, 555-123-4567, 555.123.4567
    /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
    // International format: +1-555-123-4567, +91 98765 43210
    /\+\d{1,3}[-.\s]?\d{2,4}[-.\s]?\d{3,4}[-.\s]?\d{3,4}/g,
    // Simple 10 digit: 5551234567
    /\b\d{10}\b/g,
    // With extensions: 555-123-4567 ext. 123
    /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}(\s*(ext|x|extension)\.?\s*\d{1,5})?/gi
  ];

  let allMatches: string[] = [];

  // Try all patterns
  for (const pattern of patterns) {
    const matches = text.match(pattern);
    if (matches) {
      allMatches = allMatches.concat(matches);
    }
  }

  if (allMatches.length === 0) return undefined;

  // Try to find mobile/cell phone keywords nearby
  const lowerText = text.toLowerCase();
  for (const match of allMatches) {
    const matchIndex = lowerText.indexOf(match.toLowerCase());
    if (matchIndex !== -1) {
      const contextBefore = lowerText.substring(Math.max(0, matchIndex - 30), matchIndex);
      const contextAfter = lowerText.substring(matchIndex, Math.min(lowerText.length, matchIndex + match.length + 30));
      const context = contextBefore + contextAfter;

      // Prioritize mobile/cell numbers
      if (/(mobile|cell|personal|phone)/i.test(context)) {
        return match;
      }
    }
  }

  // Return first match if no mobile found
  return allMatches[0];
}

/**
 * Extract name from text (heuristic approach)
 */
function extractName(text: string): string | undefined {
  // Try to find name in first few lines
  const lines = text.split('\n').filter(line => line.trim().length > 0);

  // Often the first line is the name
  if (lines.length > 0) {
    const firstLine = lines[0].trim();
    // Check if it looks like a name (2-4 words, starts with capital letter)
    const words = firstLine.split(/\s+/);
    if (words.length >= 2 && words.length <= 4 && /^[A-Z]/.test(firstLine)) {
      return firstLine;
    }
  }

  return undefined;
}

/**
 * Extract skills from text
 */
function extractSkills(text: string): string[] {
  const foundSkills: string[] = [];
  const lowerText = text.toLowerCase();

  for (const skill of COMMON_SKILLS) {
    const skillLower = skill.toLowerCase();
    // Check for whole word matches
    const regex = new RegExp(`\\b${skillLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (regex.test(lowerText)) {
      foundSkills.push(skill);
    }
  }

  return Array.from(new Set(foundSkills)); // Remove duplicates
}

/**
 * Extract experience sections from text
 */
function extractExperience(text: string): string[] {
  const experience: string[] = [];
  const lines = text.split('\n');

  let inExperienceSection = false;
  let currentExperience = '';

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check if we're entering experience section
    if (/^(experience|work experience|professional experience|employment)/i.test(trimmedLine)) {
      inExperienceSection = true;
      continue;
    }

    // Check if we're leaving experience section
    if (inExperienceSection && /^(education|skills|projects|certifications)/i.test(trimmedLine)) {
      if (currentExperience) {
        experience.push(currentExperience.trim());
      }
      break;
    }

    if (inExperienceSection && trimmedLine.length > 0) {
      // Look for job titles or company names
      if (/\b(engineer|developer|manager|analyst|designer|consultant|specialist|coordinator|director|lead)\b/i.test(trimmedLine)) {
        if (currentExperience) {
          experience.push(currentExperience.trim());
        }
        currentExperience = trimmedLine;
      } else {
        currentExperience += ' ' + trimmedLine;
      }
    }
  }

  if (currentExperience) {
    experience.push(currentExperience.trim());
  }

  return experience.filter(exp => exp.length > 10); // Filter out very short entries
}

/**
 * Extract education sections from text
 */
function extractEducation(text: string): string[] {
  const education: string[] = [];
  const lines = text.split('\n');

  let inEducationSection = false;
  let currentEducation = '';

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check if we're entering education section
    if (/^(education|academic|qualifications)/i.test(trimmedLine)) {
      inEducationSection = true;
      continue;
    }

    // Check if we're leaving education section
    if (inEducationSection && /^(experience|work|skills|projects|certifications)/i.test(trimmedLine)) {
      if (currentEducation) {
        education.push(currentEducation.trim());
      }
      break;
    }

    if (inEducationSection && trimmedLine.length > 0) {
      // Look for degrees
      if (/\b(bachelor|master|phd|doctorate|diploma|degree|b\.?s\.?|m\.?s\.?|m\.?b\.?a\.?|b\.?a\.?)\b/i.test(trimmedLine)) {
        if (currentEducation) {
          education.push(currentEducation.trim());
        }
        currentEducation = trimmedLine;
      } else {
        currentEducation += ' ' + trimmedLine;
      }
    }
  }

  if (currentEducation) {
    education.push(currentEducation.trim());
  }

  return education.filter(edu => edu.length > 10);
}

/**
 * Extract summary/objective from text
 */
function extractSummary(text: string): string | undefined {
  const lines = text.split('\n');
  let inSummarySection = false;
  let summary = '';

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (/^(summary|objective|profile|about)/i.test(trimmedLine)) {
      inSummarySection = true;
      continue;
    }

    if (inSummarySection && /^(experience|education|skills|work)/i.test(trimmedLine)) {
      break;
    }

    if (inSummarySection && trimmedLine.length > 0) {
      summary += trimmedLine + ' ';
    }
  }

  return summary.trim().length > 20 ? summary.trim() : undefined;
}

/**
 * Parse resume file and extract structured information
 */
export async function parseResume(file: File): Promise<ParsedResume> {
  try {
    // Extract text from file
    const extractedText = await extractTextFromFile(file);

    // Parse structured information
    const parsedResume: ParsedResume = {
      extractedText,
      name: extractName(extractedText),
      email: extractEmail(extractedText),
      phone: extractPhone(extractedText),
      skills: extractSkills(extractedText),
      experience: extractExperience(extractedText),
      education: extractEducation(extractedText),
      summary: extractSummary(extractedText)
    };

    return parsedResume;
  } catch (error) {
    console.error('Error parsing resume:', error);
    throw error;
  }
}

/**
 * Calculate match score between resume and job requirements
 */
export function calculateMatchScore(
  parsedResume: ParsedResume,
  jobRequirements: string[],
  jobBenefits?: string[]
): ScreeningResults {
  const resumeSkills = parsedResume.skills.map(s => s.toLowerCase());
  const requiredSkills = jobRequirements.map(r => r.toLowerCase());

  // Calculate skill matches
  const skillMatches: SkillMatch[] = requiredSkills.map(reqSkill => {
    const matched = resumeSkills.some(resSkill =>
      resSkill.includes(reqSkill) || reqSkill.includes(resSkill)
    );

    return {
      skill: jobRequirements[requiredSkills.indexOf(reqSkill)],
      required: true,
      matched,
      score: matched ? 100 : 0
    };
  });

  // Calculate skill match percentage
  const matchedSkillsCount = skillMatches.filter(sm => sm.matched).length;
  const skillMatchPercentage = requiredSkills.length > 0
    ? (matchedSkillsCount / requiredSkills.length) * 100
    : 50;

  // Calculate experience match (based on number of experiences listed)
  const experienceMatch = Math.min(
    (parsedResume.experience.length / 3) * 100, // Assume 3+ experiences = 100%
    100
  );

  // Calculate education match (has education = 100%, none = 50%)
  const educationMatch = parsedResume.education.length > 0 ? 100 : 50;

  // Extract keywords from resume
  const keywords = parsedResume.skills.slice(0, 10);

  // Calculate overall score (weighted average)
  const overallScore = Math.round(
    (skillMatchPercentage * 0.6) +       // 60% weight on skills
    (experienceMatch * 0.25) +            // 25% weight on experience
    (educationMatch * 0.15)               // 15% weight on education
  );

  return {
    overallScore,
    skillMatches,
    experienceMatch: Math.round(experienceMatch),
    educationMatch: Math.round(educationMatch),
    keywords
  };
}

/**
 * Get a color based on match score
 */
export function getScoreColor(score: number): 'error' | 'warning' | 'success' {
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'error';
}

/**
 * Get a label based on match score
 */
export function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent Match';
  if (score >= 70) return 'Good Match';
  if (score >= 50) return 'Fair Match';
  if (score >= 30) return 'Weak Match';
  return 'Poor Match';
}
